import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, post } from './api';
import { Modal } from './ui';

/**
 * "Email…" (owner 10/22): a PDF of the Week summary (one or more weeks), the Day board (chosen days or the whole
 * week) or the Rotation (chosen days), from the MyWunderland mailbox (replies to the store), to people picked from the store's address book.
 */

type What = 'week' | 'day' | 'rotation';
interface Setup { from: string | null; replyTo?: string | null; demo?: boolean; ready: boolean; why: string | null; canSend: boolean; contacts: { id: number; name: string; email: string }[]; weeks: { start: string; label: string }[];
  recent?: { at: string; subject: string; to: string[]; by: string | null; status: 'sent' | 'failed' | 'demo' }[] }

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function isoAdd(iso: string, n: number) { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); }
const md = (iso: string) => { const [, m, d] = iso.split('-'); return `${Number(m)}/${Number(d)}`; };
const list = (xs: string[]) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`);

/** Same wording as the server makes (server/src/routes/email.ts subjectFor). */
function subjectFor(store: string, what: What, weeks: string[], dates: string[]) {
  const end = (ws: string) => md(isoAdd(ws, 6));
  if (what === 'week') return `${store} weekly schedule ${weeks.length > 1 ? 'summaries' : 'summary'} ending ${list(weeks.map(end))}`;
  const whole = dates.length === 7;
  const ws = isoAdd(dates[0] ?? '2000-01-02', -new Date((dates[0] ?? '2000-01-02') + 'T12:00:00Z').getUTCDay());
  if (what === 'day') return whole ? `${store} day board, week ending ${end(ws)}` : `${store} day board ${list(dates.map(md))}`;
  return whole ? `${store} daily rotation, week ending ${end(ws)}` : `${store} daily rotation ${list(dates.map(md))}`;
}

export function EmailPdf({ locationId, storeName, what, weekStart, date, onClose }: {
  locationId: number; storeName: string; what: What; weekStart: string; date?: string; onClose: () => void;
}) {
  const [s, setS] = useState<Setup | null>(null);
  const [err, setErr] = useState('');
  const [weeks, setWeeks] = useState<string[]>([weekStart]);
  const [dates, setDates] = useState<string[]>(date ? [date] : [weekStart]);
  const [to, setTo] = useState<Record<number, boolean>>({});
  const [note, setNote] = useState('');
  const [adding, setAdding] = useState<{ name: string; email: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const load = useCallback(async () => {
    try { setS(await get<Setup>(`/api/email/setup?location=${locationId}`)); } catch (e) { setErr(e instanceof ApiError ? e.message : 'Can’t load the email settings.'); }
  }, [locationId]);
  useEffect(() => { void load(); }, [load]);

  const week7 = Array.from({ length: 7 }, (_, i) => isoAdd(weekStart, i));
  const toggleDate = (d: string) => setDates((x) => (x.includes(d) ? x.filter((y) => y !== d) : [...x, d].sort()));
  const toggleWeek = (w: string) => setWeeks((x) => (x.includes(w) ? x.filter((y) => y !== w) : [...x, w].sort()));
  const ids = (s?.contacts ?? []).filter((c) => to[c.id]).map((c) => c.id);
  const picked = what === 'week' ? weeks.length : dates.length;
  const subject = subjectFor(storeName, what, weeks, dates);

  const addContact = async () => {
    if (!adding) return;
    try {
      const r = await post<{ id: number; contacts: Setup['contacts'] }>('/api/email/contacts', { locationId, name: adding.name, email: adding.email });
      setS((x) => (x ? { ...x, contacts: r.contacts } : x)); setTo((t) => ({ ...t, [r.id]: true })); setAdding(null); setErr('');
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t save.'); }
  };
  const remove = async (id: number) => {
    try { const r = await post<{ contacts: Setup['contacts'] }>(`/api/email/contacts/${id}/remove`); setS((x) => (x ? { ...x, contacts: r.contacts } : x)); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t save.'); }
  };
  const send = async () => {
    setBusy(true); setErr('');
    try {
      const r = await post<{ subject: string; to: string[]; demo?: boolean }>('/api/email/send', { locationId, what, contactIds: ids, note: note.trim() || undefined, ...(what === 'week' ? { weekStarts: weeks } : { dates }) });
      setDone(r.demo ? `✓ “${r.subject}” is in the Demo outbox (not really emailed).` : `✓ Sent “${r.subject}” to ${list(r.to)}.`);
      // owner 10/28: clear what was sent, and close the box by itself a moment later
      setNote(''); setTo({});
      setTimeout(onClose, 2500);
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'The email didn’t go out. Try again.'); }
    finally { setBusy(false); }
  };

  const title = what === 'week' ? 'Email the week summary' : what === 'day' ? 'Email the day board' : 'Email the rotation';
  return (
    <Modal title={title} onClose={onClose} wide>
      {s && <div style={{ fontSize: 13, color: '#4A5561' }}>{s.demo ? <>This is the Demo store: nothing is really emailed. What would have gone out shows in its Demo outbox.</> : s.from ? <>Sent as a PDF from <b>{s.from}</b>; replies go to {s.replyTo ? <b>{s.replyTo}</b> : 'the store'}.</> : null}</div>}
      {s && !s.ready && <div className="sharebar" style={{ background: '#FCEFD9', borderColor: '#EBC98A', color: '#5C3A05' }}>{s.why}</div>}
      {done && <div className="sharebar" style={{ background: '#EEF7EE', borderColor: '#BDE8CD' }}>{done}</div>}
      {err && <div className="errbar">{err}</div>}

      <div className="emsec"><b>{what === 'week' ? 'Which weeks' : 'Which days'}</b>
        {what === 'week' ? (
          <div className="emchecks">
            {(s?.weeks ?? []).map((w) => <label key={w.start} className="allday"><input type="checkbox" checked={weeks.includes(w.start)} onChange={() => toggleWeek(w.start)} />{w.label}</label>)}
          </div>
        ) : (
          <div className="emchecks">
            {week7.map((d, i) => <label key={d} className="allday"><input type="checkbox" checked={dates.includes(d)} onChange={() => toggleDate(d)} />{DAYS[i]} {md(d)}</label>)}
            <button className="elink" onClick={() => setDates(dates.length === 7 ? (date ? [date] : []) : week7)}>{dates.length === 7 ? 'Just one day' : 'Whole week'}</button>
          </div>
        )}
      </div>

      <div className="emsec"><b>Send to</b>
        <div className="emchecks">
          {(s?.contacts ?? []).map((c) => (
            <span key={c.id} className="emcontact">
              <label className="allday"><input type="checkbox" checked={!!to[c.id]} onChange={() => setTo((t) => ({ ...t, [c.id]: !t[c.id] }))} />{c.name} <span style={{ color: '#4A5561' }}>{c.email}</span></label>
              {s?.canSend && <button className="xbtn" aria-label={`Remove ${c.name} from the address book`} title="Remove from the address book" onClick={() => void remove(c.id)}>×</button>}
            </span>
          ))}
          {s && !s.contacts.length && <span className="stnone">The address book is empty. Add the people who should get these emails.</span>}
        </div>
        {s?.canSend && (adding ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <input className="inp" placeholder="Name" value={adding.name} onChange={(e) => setAdding({ ...adding, name: e.target.value })} aria-label="Name" style={{ maxWidth: 200 }} />
            <input className="inp" placeholder="name@example.com" value={adding.email} onChange={(e) => setAdding({ ...adding, email: e.target.value })} aria-label="Email address" style={{ maxWidth: 260 }} type="email" />
            <button className="tbtn" disabled={!adding.name.trim() || !adding.email.trim()} onClick={() => void addContact()}>Add to address book</button>
            <button className="btn2" onClick={() => setAdding(null)}>Cancel</button>
          </div>
        ) : <button className="btn2" style={{ alignSelf: 'flex-start' }} onClick={() => setAdding({ name: '', email: '' })}>Add someone…</button>)}
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Message (optional)
        <textarea className="inp" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} style={{ minHeight: 60 }} placeholder="e.g. Here’s next week’s schedule." /></label>
      <div style={{ fontSize: 13 }}><span style={{ color: '#4A5561' }}>Subject:</span> <b>{picked ? subject : '…'}</b></div>
      {!!s?.recent?.length && (
        <details style={{ fontSize: 13, color: '#4A5561' }}>
          <summary style={{ cursor: 'pointer' }}>Sent from {storeName} lately ({s.recent.length})</summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
            {s.recent.map((m, i) => (
              <span key={i}>{m.at} · <b style={{ color: '#17212B' }}>{m.subject}</b> → {m.to.join(', ')}{m.by ? ` · by ${m.by}` : ''}{m.status === 'failed' ? ' · didn’t go out' : m.status === 'demo' ? ' · Demo outbox' : ''}</span>
            ))}
          </div>
        </details>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn2" onClick={onClose}>{done ? 'Done' : 'Cancel'}</button>
        <button className="tbtn primary" disabled={!s?.ready || !s.canSend || !ids.length || !picked || busy} onClick={() => void send()}>{busy ? 'Sending…' : 'Send email'}</button>
      </div>
    </Modal>
  );
}
